import type { Ref } from "@typegoose/typegoose";
import { ONLINE_STATUS, type DocumentReference, type FirstPartyDeviceType } from "../../shared.js";
import type { Device } from "./device.js";
import type { SensorSpecifier } from "./subSchemas/sensorSpecifier.js";
import { DeviceTypeConfigurations } from "../../Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { ValveState } from "../commonSubSchemas/valveState.js";
import { minuteMs } from "../../timeConstants.js";
import { VerificationCheckConfiguration } from "./subSchemas/verificationCheckConfiguration.js";
import { SproutCheckType } from "./subSchemas/sproutVerification/generalSproutVerificationCheck.js";
/**
 * Returns the online status of the device based on its warnings and last contact date.
 * `referenceTime` is accepted for test determinism but is not used in comparisons —
 * status is determined solely by warnings.
 */
export const getOnlineStatus = (self: Device, _referenceTime?: Date): ONLINE_STATUS => {
    if (!self.lastContact) {
        return ONLINE_STATUS.AwaitingConnection;
    }
    if (self.warnings?.some((w) => w.warningType === "waiting for device")) {
        return ONLINE_STATUS.AwaitingConnection;
    }
    if (self.warnings?.some((w) => w.warningType === "offline")) {
        return ONLINE_STATUS.Offline;
    }
    return ONLINE_STATUS.Online;
};

/**
 * Gets connected zone IDs for a specific sensor
 */
export const getSensorsConnectedZoneIDs = (self: Device, sensor: SensorSpecifier): DocumentReference[] => {
    let hasDefault = false;
    const connectedZoneIDs: Set<Ref<any>> = new Set();
    if (sensor.connectedZones && sensor.connectedZones.length > 0) {
        sensor.connectedZones.forEach((cz) => {
            if (cz === "default") {
                hasDefault = true;
            } else {
                connectedZoneIDs.add(cz);
            }
        });
    } else {
        hasDefault = true;
    }
    if (hasDefault) {
        const valveCount = DeviceTypeConfigurations[self.DeviceType as FirstPartyDeviceType].valveCount;
        if (valveCount === 1) {
            if (self.connectedZones) {
                self.connectedZones.forEach((cz) => {
                    if (cz && cz.toString()) {
                        connectedZoneIDs.add(cz);
                    }
                });
            }
        } else {
            const sensorIndex = getIndexOfSensor(self, sensor);
            const potentialID = self.connectedZones?.[sensorIndex];
            if (potentialID) {
                connectedZoneIDs.add(potentialID);
            }
        }
    }
    return removeDuplicateIDs(Array.from(connectedZoneIDs));
};

/**
 * Gets the index of a sensor in the connectedSensors array
 */
export const getIndexOfSensor = (self: Device, sensor: SensorSpecifier): number => {
    if (!self.connectedSensors || !sensor) {
        return -1;
    }

    if (sensor.sensorIndex !== undefined) {
        return sensor.sensorIndex;
    }

    return self.connectedSensors.findIndex((v) => v.sensorType === sensor.sensorType);
};

function removeDuplicateIDs<Type>(array: Type[]): Type[] {
    for (let i = 0; i < array.length; i++) {
        for (let j = i + 1; j < array.length; j++) {
            if (String(array[i]) === String(array[j])) {
                array.splice(j, 1);
                j = i + 1;
            }
        }
    }
    return array;
}

export const updateLastValveStateChange = ({
    device,
    newValveState,
    transitionTime,
    currentTime,
}: {
    device: Device;
    newValveState: ValveState;
    transitionTime: Date;
    currentTime: Date;
}): void => {
    if (
        device.valveState &&
        device.valveState.open[0] === newValveState.open[0] &&
        device.valveState.open[1] === newValveState.open[1]
    ) {
        // we don't need to flag this as a valve transition.
        return;
    }
    // if this transition time is before when we know the valve transitioned previously, we need to throw out the time
    if (device.lastValveStateChange) {
        if (transitionTime.valueOf() + minuteMs < new Date(device.lastValveStateChange).valueOf()) {
            transitionTime = currentTime;
        }
    }
    device.lastValveStateChange = transitionTime;
};

/**
 * Retrieves a verification check configuration by its type from the specified configurations.
 *
 * @param model - The device model containing verification check configurations
 * @param checkType - The type of verification check to find
 * @returns The matching verification check configuration, or null if not found
 */
export const getVerificationCheckConfiguration = (
    model: Pick<Device, "verificationCheckConfigurations">,
    checkType: SproutCheckType,
): VerificationCheckConfiguration | null =>
    model.verificationCheckConfigurations.find((configuration) => configuration.verificationCheckType === checkType) ??
    null;

/**
 * Maps a new array from the specified configurations with the new verification check configuration merged over any existing value.
 *
 * This function filters out any existing configuration with the same verification check type as the new configuration,
 * adds the new configuration to the array, and then sorts the array by verification check type alphabetically to ensure
 * eventual consistency in ordering.
 *
 * @param model - The device model containing verification check configurations
 * @param newConfiguration - The new verification check configuration to merge into the array
 * @returns A new sorted array of verification check configurations with the new configuration included
 */
export const mapAndSortNewVerificationCheckConfiguration = (
    model: Pick<Device, "verificationCheckConfigurations">,
    newConfiguration: VerificationCheckConfiguration,
): VerificationCheckConfiguration[] => {
    const newConfigurations = [
        ...model.verificationCheckConfigurations.filter(
            (value) => value.verificationCheckType !== newConfiguration.verificationCheckType,
        ),
    ];
    newConfigurations.push(newConfiguration);
    return newConfigurations.sort((a, b) => a.verificationCheckType.localeCompare(b.verificationCheckType));
};
